import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import Header from "../../../Common/Layout/Header";
import { useFlightSearch } from "../../hooks/useFlightSearch";
import styles from "./Flights5.module.css";
import {
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightLine,
  RiFilterLine,
  RiTimeLine,
  RiMoneyDollarCircleLine,
  RiCheckLine,
  RiCloseLine,
  RiArrowDownSLine,
  RiSuitcaseLine,
  RiRefreshLine,
  RiCalendar2Line,
  RiUserLine,
  RiPlaneLine,
  RiInformationLine,
  RiAlertLine
} from "react-icons/ri";

/* ─── Utility ─────────────────────────────────────────────────────────── */
const formatTime = (dt) => {
  if (!dt) return "--:--";
  try { return moment(dt).format("HH:mm"); } catch { return "--:--"; }
};

const formatDuration = (mins) => {
  if (!mins && mins !== 0) return "N/A";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m > 0 ? m + "m" : ""}`.trim();
};

const formatPrice = (val) => {
  if (!val && val !== 0) return "0";
  return Number(val).toLocaleString("en-IN");
};

const getMinPrice = (flightLeg) => {
  if (!flightLeg?.dcPriceDetails?.length) return 0;
  return Math.min(...flightLeg.dcPriceDetails.map((fare) => fare.totalPrice || Infinity));
};

/* ─── Component ───────────────────────────────────────────────────────── */
const Flights5 = ({ mode, onNavigate, templateId, params: builderParams, styleConfig, companyId }) => {
  const urlParams = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const finalCompanyId = (mode === "builder" || mode === "preview")
    ? (user?.isSupplierAPI === true ? user?.cid : user?.parentCompanyId)
    : companyId;

  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#e05c2a",
    "--secondary-color": styleConfig?.secondary || "#f28a5b",
  };

  const params = builderParams || urlParams;
  const { adults = 1, kids = 0, infants = 0, isDomestic = "DOMESTIC" } = params;

  const actualSearchQuery = mode === "builder" ? builderParams?.searchQuery : location.state?.searchQuery || {};

  const [searchQuery, setSearchQuery] = useState({
    ...actualSearchQuery,
    companyId: finalCompanyId || actualSearchQuery.companyId
  });

  useEffect(() => {
    if (!actualSearchQuery || Object.keys(actualSearchQuery).length === 0) {
      if (mode === "live") navigate("/template/flight/search");
    } else {
      setSearchQuery({
        ...actualSearchQuery,
        companyId: finalCompanyId || actualSearchQuery.companyId
      });
    }
  }, [actualSearchQuery, mode, navigate, finalCompanyId]);

  const journeyType = searchQuery?.journeyType || "ONEWAY";
  const isRoundTrip = journeyType === "RETURN";

  /* ── State ── */
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFares, setSelectedFares] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [expandTab, setExpandTab] = useState("fares");
  const [selectedFareIdxMap, setSelectedFareIdxMap] = useState({});
  const [selectedFaresRoundTrip, setSelectedFaresRoundTrip] = useState({});

  /* ── REAL API CALL VIA HOOK ── */
  const { data: flightData = [], isLoading: pageLoading } = useFlightSearch(searchQuery);

  /* ── Filters ── */
  const [filterStops, setFilterStops] = useState([]);
  const [filterAirlines, setFilterAirlines] = useState([]);
  const [filterMaxPrice, setFilterMaxPrice] = useState(null);
  const [filterTimeSlots, setFilterTimeSlots] = useState([]);
  const [sortBy, setSortBy] = useState("price");
  const [page, setPage] = useState(1);
  const PER_PAGE = 8;

  const currencySymbol = "₹";
  const currencyCode = "INR";

  useEffect(() => { setPage(1); }, [filterStops, filterAirlines, filterMaxPrice, filterTimeSlots, sortBy, activeTab]);

  const activeFlights = useMemo(() => {
    return flightData[activeTab]?.dcFlightLegs || [];
  }, [flightData, activeTab]);

  const airlines = useMemo(() => {
    const map = {};
    activeFlights.forEach((f) => {
      const name = f.dcDisplayLegs?.[0]?.airlineData?.airline || "Unknown";
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map).map(([name, count]) => ({ name, count }));
  }, [activeFlights]);

  const maxPrice = useMemo(() => {
    if (!activeFlights.length) return 50000;
    return Math.max(...activeFlights.map((f) => getMinPrice(f)));
  }, [activeFlights]);

  useEffect(() => {
    if (maxPrice > 0 && filterMaxPrice === null) setFilterMaxPrice(maxPrice);
  }, [maxPrice, filterMaxPrice]);

  const filtered = useMemo(() => {
    let list = [...activeFlights];
    if (filterStops.length) list = list.filter((f) => filterStops.includes(f?.dcDisplayLegs?.[0]?.stops ?? 0));
    if (filterAirlines.length) list = list.filter((f) => filterAirlines.includes(f?.dcDisplayLegs?.[0]?.airlineData?.airline));
    if (filterMaxPrice !== null) list = list.filter((f) => getMinPrice(f) <= filterMaxPrice);
    if (filterTimeSlots.length) {
      list = list.filter((f) => {
        const dt = f?.dcDisplayLegs?.[0]?.startSegment?.departureDateTime;
        if (!dt) return false;
        const h = new Date(dt).getHours();
        return filterTimeSlots.some((slot) => {
          if (slot === "early") return h >= 0 && h < 6;
          if (slot === "morn") return h >= 6 && h < 12;
          if (slot === "after") return h >= 12 && h < 18;
          if (slot === "eve") return h >= 18;
          return false;
        });
      });
    }
    list.sort((a, b) => {
      const priceA = getMinPrice(a), priceB = getMinPrice(b);
      const durA = a?.dcDisplayLegs?.[0]?.totalDuration || 0;
      const durB = b?.dcDisplayLegs?.[0]?.totalDuration || 0;
      const depA = new Date(a?.dcDisplayLegs?.[0]?.startSegment?.departureDateTime || 0);
      const depB = new Date(b?.dcDisplayLegs?.[0]?.startSegment?.departureDateTime || 0);
      const arrA = new Date(a?.dcDisplayLegs?.[0]?.endSegment?.arrivalDateTime || 0);
      const arrB = new Date(b?.dcDisplayLegs?.[0]?.endSegment?.arrivalDateTime || 0);
      if (sortBy === "price") return priceA - priceB;
      if (sortBy === "duration") return durA - durB;
      if (sortBy === "depart") return depA - depB;
      if (sortBy === "arrive") return arrA - arrB;
      return 0;
    });
    return list;
  }, [activeFlights, filterStops, filterAirlines, filterMaxPrice, filterTimeSlots, sortBy]);

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const toggleStop = (val) => setFilterStops((p) => (p.includes(val) ? p.filter((x) => x !== val) : [...p, val]));
  const toggleAirline = (val) => setFilterAirlines((p) => (p.includes(val) ? p.filter((x) => x !== val) : [...p, val]));
  const toggleTime = (val) => setFilterTimeSlots((p) => (p.includes(val) ? p.filter((x) => x !== val) : [...p, val]));
  const clearFilters = () => { setFilterStops([]); setFilterAirlines([]); setFilterMaxPrice(maxPrice); setFilterTimeSlots([]); };
  const toggleExpand = (id) => { setExpandedId((p) => (p === id ? null : id)); setExpandTab("fares"); };

  const routeToDetails = (payload, passedJourneyType) => {
    if (mode === "builder") {
      onNavigate({ name: "flight-details", params: { searchQuery, payload, journeyType: passedJourneyType } });
    } else if (mode === "preview") {
      navigate(`/template-preview/${templateId}/flight-details`, { state: { searchQuery, payload, journeyType: passedJourneyType } });
    } else {
      navigate(`/template/flight-details/${adults}/${kids}/${infants}/${isDomestic}`, { state: { searchQuery, payload, journeyType: passedJourneyType } });
    }
  };

  const handleSelectFare = (passedJourneyType, flightLeg, fare, isOutbound = true) => {
    setSelectedFares((prev) => {
      if (!isRoundTrip) return { [passedJourneyType]: { flightLeg, fare } };
      const newSelection = { ...prev };
      const routeKey = isOutbound ? "outbound" : "return";
      newSelection[routeKey] = { flightLeg, fare, journeyType: passedJourneyType, route: routeKey, locationFrom: flightLeg?.dcDisplayLegs?.[0]?.startSegment?.airportCode, locationTo: flightLeg?.dcDisplayLegs?.[0]?.endSegment?.airportCode };
      return newSelection;
    });
  };

  const handleProceedToBooking = () => {
    const payload = [
      { flightLeg: selectedFaresRoundTrip.outbound.flightLeg, fare: selectedFaresRoundTrip.outbound.fare, journeyType: selectedFaresRoundTrip.outbound.journeyType, route: "outbound" },
      { flightLeg: selectedFaresRoundTrip.return.flightLeg, fare: selectedFaresRoundTrip.return.fare, journeyType: selectedFaresRoundTrip.return.journeyType, route: "return" }
    ];
    routeToDetails(payload, "RETURN");
  };

  const handleBookFlight = (flightLeg, e) => {
    e?.stopPropagation();
    const isOutbound = activeTab === 0;
    const currentJourneyType = flightData[activeTab]?.journeyType || "ONEWAY";
    const selectedFareIdx = selectedFareIdxMap[flightLeg.supplierLegId] ?? 0;
    const cheapestFare = flightLeg.dcPriceDetails?.[selectedFareIdx] || [...flightLeg.dcPriceDetails].sort((a, b) => a.totalPrice - b.totalPrice)[0];
    handleSelectFare(currentJourneyType, flightLeg, cheapestFare, isOutbound);
    if (!isRoundTrip) {
      routeToDetails([{ flightLeg, fare: cheapestFare, journeyType: currentJourneyType }], currentJourneyType);
    } else {
      const routeKey = isOutbound ? "outbound" : "return";
      const otherRouteKey = isOutbound ? "return" : "outbound";
      setSelectedFaresRoundTrip((prev) => ({ ...prev, [routeKey]: { flightLeg, fare: cheapestFare, journeyType: currentJourneyType, locationFrom: flightLeg?.dcDisplayLegs?.[0]?.startSegment?.airportCode, locationTo: flightLeg?.dcDisplayLegs?.[0]?.endSegment?.airportCode } }));
      if (!selectedFaresRoundTrip[otherRouteKey]) {
        alert(`Please select your ${isOutbound ? "return" : "outbound"} flight as well.`);
        if (flightData.length > 1) setActiveTab(isOutbound ? 1 : 0);
      }
    }
  };

  const origin = searchQuery?.flightTravelerPoints?.[0]?.from || "—";
  const destination = searchQuery?.flightTravelerPoints?.[0]?.to || "—";
  const depDate = searchQuery?.flightTravelerPoints?.[0]?.departureDate || "";
  const totalPax = Number(adults) + Number(kids) + Number(infants);

  const SORT_OPTIONS = [
    { value: "price", label: "Cheapest", icon: <RiMoneyDollarCircleLine /> },
    { value: "duration", label: "Fastest", icon: <RiTimeLine /> },
    { value: "depart", label: "Departure", icon: <RiFlightTakeoffLine /> },
    { value: "arrive", label: "Arrival", icon: <RiFlightLandLine /> },
  ];

  const STOP_OPTIONS = [
    { value: 0, label: "Non-stop" },
    { value: 1, label: "1 Stop" },
    { value: 2, label: "2+ Stops" },
  ];

  const TIME_SLOTS = [
    { value: "early", label: "Early Bird", icon: "🌙", time: "12am–6am" },
    { value: "morn", label: "Morning", icon: "🌅", time: "6am–12pm" },
    { value: "after", label: "Afternoon", icon: "☀️", time: "12pm–6pm" },
    { value: "eve", label: "Evening", icon: "🌆", time: "6pm–12am" },
  ];

  if (!searchQuery || Object.keys(searchQuery).length === 0) {
    return (
      <div className={styles.page} style={themeVariables}>
        <Header />
        <div className={styles.errorWrap}>
          <div className={styles.errorBox}>
            <RiAlertLine className={styles.errorIcon} />
            <h2 className={styles.errorTitle}>Search Session Expired</h2>
            <p className={styles.errorSub}>Your flight search details were lost. Please search again.</p>
            <button className={styles.errorBtn} onClick={() => mode === "builder" ? onNavigate({ name: "flight-search" }) : navigate(-1)}>
              Go Back to Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authLoading) return (
    <div className={styles.fullPageCenter}>
      <div className={styles.loadingSpinner} />
    </div>
  );

  /* ─── RENDER ─── */
  return (
    <div className={styles.page} style={themeVariables}>

      {/* ── Sticky Top Bar ── */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.topBarRoute}>
            <span className={styles.topBarCity}>{origin}</span>
            <RiArrowRightLine className={styles.topBarArrow} />
            <span className={styles.topBarCity}>{destination}</span>
          </div>

          <div className={styles.topBarMeta}>
            {depDate && (
              <span className={styles.topBarPill}>
                <RiCalendar2Line className={styles.topBarPillIcon} />
                {moment(depDate).format("DD MMM YY")}
              </span>
            )}
            <span className={styles.topBarPill}>
              <RiUserLine className={styles.topBarPillIcon} />
              {totalPax} Traveller{totalPax !== 1 ? "s" : ""}
            </span>
            <span className={styles.topBarPill}>
              <RiPlaneLine className={styles.topBarPillIcon} />
              {searchQuery?.cabinClass || "ECONOMY"}
            </span>
            {isDomestic === "INTERNATIONAL" && (
              <span className={styles.topBarPill}>
                <RiInformationLine className={styles.topBarPillIcon} />
                International
              </span>
            )}
          </div>

          <div className={styles.topBarSpacer} />

          <button className={styles.modifyBtn} onClick={() => { if (mode === "builder") onNavigate({ name: "flight-search", params: null }); else navigate(-1); }}>
            <RiFilterLine /> Modify Search
          </button>
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div className={styles.main}>

        {/* ── SIDEBAR ── */}
        <aside className={styles.sidebar}>

          {/* Stops */}
          <div className={styles.filterCard}>
            <div className={styles.filterCardTitle}>
              <span className={styles.filterCardLabel}>Stops</span>
              {filterStops.length > 0 && <button className={styles.filterClearBtn} onClick={() => setFilterStops([])}>Clear</button>}
            </div>
            <div className={styles.stopChips}>
              {STOP_OPTIONS.map((s) => (
                <div key={s.value} className={`${styles.stopChip} ${filterStops.includes(s.value) ? styles.stopChipActive : ""}`} onClick={() => toggleStop(s.value)}>
                  <div className={styles.stopChipLeft}>
                    <div className={styles.stopChipDot} />
                    <span className={styles.stopChipName}>{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Price Range */}
          {maxPrice > 0 && (
            <div className={styles.filterCard}>
              <div className={styles.filterCardTitle}>
                <span className={styles.filterCardLabel}>Max Price</span>
                {filterMaxPrice < maxPrice && <button className={styles.filterClearBtn} onClick={() => setFilterMaxPrice(maxPrice)}>Reset</button>}
              </div>
              <div className={styles.priceRange}>
                <div className={styles.priceRangeValues}>
                  <span className={styles.priceRangeMin}>{currencySymbol}0</span>
                  <span className={styles.priceRangeMax}>{currencySymbol}{formatPrice(filterMaxPrice)}</span>
                </div>
                <input type="range" className={styles.rangeSlider} min={0} max={maxPrice} step={100} value={filterMaxPrice ?? maxPrice} onChange={(e) => setFilterMaxPrice(Number(e.target.value))} style={{ "--val": `${((filterMaxPrice ?? maxPrice) / maxPrice) * 100}%` }} />
              </div>
            </div>
          )}

          {/* Departure Time */}
          <div className={styles.filterCard}>
            <div className={styles.filterCardTitle}>
              <span className={styles.filterCardLabel}>Departure Time</span>
              {filterTimeSlots.length > 0 && <button className={styles.filterClearBtn} onClick={() => setFilterTimeSlots([])}>Clear</button>}
            </div>
            <div className={styles.timeSlots}>
              {TIME_SLOTS.map((t) => (
                <div key={t.value} className={`${styles.timeSlot} ${filterTimeSlots.includes(t.value) ? styles.timeSlotActive : ""}`} onClick={() => toggleTime(t.value)}>
                  <span className={styles.timeSlotIcon}>{t.icon}</span>
                  <span className={styles.timeSlotLabel}>{t.label}</span>
                  <span className={styles.timeSlotTime}>{t.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Airlines */}
          {airlines.length > 0 && (
            <div className={styles.filterCard}>
              <div className={styles.filterCardTitle}>
                <span className={styles.filterCardLabel}>Airlines</span>
                {filterAirlines.length > 0 && <button className={styles.filterClearBtn} onClick={() => setFilterAirlines([])}>Clear</button>}
              </div>
              <div className={styles.airlineList}>
                {airlines.map((a) => (
                  <div key={a.name} className={styles.airlineItem} onClick={() => toggleAirline(a.name)}>
                    <div className={`${styles.airlineCheckbox} ${filterAirlines.includes(a.name) ? styles.airlineCheckboxActive : ""}`}>
                      {filterAirlines.includes(a.name) && <RiCheckLine className={styles.airlineCheckIcon} />}
                    </div>
                    <span className={styles.airlineName}>{a.name}</span>
                    <span className={styles.airlineCount}>({a.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clear All */}
          {(filterStops.length || filterAirlines.length || filterTimeSlots.length || (filterMaxPrice !== null && filterMaxPrice < maxPrice)) ? (
            <button className={styles.modifyBtn} style={{ width: "100%", justifyContent: "center" }} onClick={clearFilters}>
              <RiRefreshLine /> Clear All Filters
            </button>
          ) : null}
        </aside>

        {/* ── RESULTS ── */}
        <div className={styles.resultsPanel}>

          {/* Header & Tabs */}
          <div className={styles.resultsHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <span className={styles.resultCount}>
                <span className={styles.resultCountNum}>{filtered.length} </span>
                <span className={styles.resultCountAccent}>flights</span> found
              </span>

              {isRoundTrip && flightData && flightData.length > 1 && (
                <div style={{ display: "flex", gap: "8px" }}>
                  {flightData.map((item, index) => (
                    <button key={index} className={`${styles.sortChip} ${activeTab === index ? styles.sortChipActive : ""}`} onClick={() => setActiveTab(index)}>
                      {index === 0 ? "Outbound" : "Return"}: {item.locationFrom || "FROM"} → {item.locationTo || "TO"}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.sortSpacer} />
            <span className={styles.sortLabel}>Sort by</span>
            <div className={styles.sortChips}>
              {SORT_OPTIONS.map((s) => (
                <button key={s.value} className={`${styles.sortChip} ${sortBy === s.value ? styles.sortChipActive : ""}`} onClick={() => setSortBy(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Loading */}
          {pageLoading && (
            <div className={styles.loadingWrap}>
              <div className={styles.loadingSpinner} />
              <div className={styles.loadingText}>Scanning best routes for you…</div>
            </div>
          )}

          {/* Empty */}
          {!pageLoading && filtered.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}><RiPlaneLine /></div>
              <div className={styles.emptyTitle}>No flights match your filters</div>
              <div className={styles.emptySub}>Try adjusting your filters or clear them to see all available flights.</div>
              <button className={styles.modifyBtn} style={{ marginTop: 16 }} onClick={clearFilters}>
                <RiRefreshLine /> Clear Filters
              </button>
            </div>
          )}

          {/* Flight Cards */}
          {!pageLoading && paginated.map((flightLeg, idx) => {
            const displayLeg = flightLeg?.dcDisplayLegs?.[0];
            if (!displayLeg) return null;

            const airlineData = displayLeg?.airlineData;
            const startSeg = displayLeg?.startSegment;
            const endSeg = displayLeg?.endSegment;
            const priceDetails = flightLeg?.dcPriceDetails || [];
            const minPrice = flightLeg.totalPrice || 0;

            const isExpanded = expandedId === flightLeg.supplierLegId;
            const selectedFareIdx = selectedFareIdxMap[flightLeg.supplierLegId] ?? 0;
            const fare = priceDetails[selectedFareIdx] || priceDetails[0];

            const stops = displayLeg?.stops || 0;
            const stopClass = stops === 0 ? styles.stopNone : stops === 1 ? styles.stopOne : styles.stopTwo;

            const isOutbound = activeTab === 0;
            const currentJourneyType = flightData[activeTab]?.journeyType || "ONEWAY";
            const isSelected = isRoundTrip
              ? (isOutbound ? selectedFaresRoundTrip.outbound?.flightLeg?.supplierLegId : selectedFaresRoundTrip.return?.flightLeg?.supplierLegId) === flightLeg.supplierLegId
              : selectedFares[currentJourneyType]?.flightLeg?.supplierLegId === flightLeg.supplierLegId;

            return (
              <div
                key={flightLeg.supplierLegId || idx}
                className={`${styles.flightCard} ${isSelected ? styles.flightCardSelected : ""}`}
                onClick={() => toggleExpand(flightLeg.supplierLegId)}
              >
                {isSelected && <div className={styles.selectedTag}>✦ Selected</div>}

                {/* Main row */}
                <div className={styles.flightCardMain}>
                  {/* Airline */}
                  <div className={styles.airlineInfo}>
                    <div className={styles.airlineLogo}>
                      <img
                        src={`/assets/images/airlinelogos/${airlineData?.airlineCode || "default"}.png`}
                        alt={airlineData?.airline}
                        className={styles.airlineLogoImg}
                        onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:11px;font-weight:800;color:#e05c2a;">${airlineData?.airlineCode || "FL"}</span>`; }}
                      />
                    </div>
                    <div className={styles.airlineDetails}>
                      <div className={styles.airlineName}>{airlineData?.airline || "UNKNOWN"}</div>
                      <div className={styles.flightNum}>{airlineData?.flightNumber || "N/A"}</div>
                    </div>
                  </div>

                  {/* Route */}
                  <div className={styles.routeBlock}>
                    <div className={styles.timeBlock}>
                      <div className={styles.timeMain}>{formatTime(startSeg?.departureDateTime)}</div>
                      <div className={styles.timeAirport}>{startSeg?.airportCode || origin}</div>
                      <div className={styles.timeCity}>{startSeg?.departureDateTime ? moment(startSeg.departureDateTime).format("DD MMM") : "---"}</div>
                    </div>

                    <div className={styles.durationTrack}>
                      <div className={styles.durationTime}>{formatDuration(displayLeg?.totalDuration)}</div>
                      <div className={styles.trackLine}>
                        <div className={styles.trackDot} />
                        <div className={styles.trackLineBar}>
                          {stops === 0 ? <RiPlaneLine className={styles.trackPlane} /> : <div className={styles.trackStopDot} />}
                        </div>
                        <div className={styles.trackDot} />
                      </div>
                      <div className={`${styles.stopInfo} ${stopClass}`}>
                        {stops === 0 ? "Non-stop" : `${stops} Stop${stops > 1 ? "s" : ""}`}
                      </div>
                    </div>

                    <div className={styles.timeBlock}>
                      <div className={styles.timeMain}>{formatTime(endSeg?.arrivalDateTime)}</div>
                      <div className={styles.timeAirport}>{endSeg?.airportCode || destination}</div>
                      <div className={styles.timeCity}>{endSeg?.arrivalDateTime ? moment(endSeg.arrivalDateTime).format("DD MMM") : "---"}</div>
                    </div>
                  </div>

                  {/* Price */}
                  <div className={styles.priceBlock}>
                    <div className={styles.priceLabel}>Per person</div>
                    <div className={styles.priceMain}>
                      <span className={styles.priceCurrency}>{currencySymbol}</span>
                      {formatPrice(fare?.totalPrice ?? minPrice)}
                    </div>
                    <div className={styles.priceSub}>{fare?.fareIdentifier || "PUBLISHED"}</div>
                    <button className={styles.bookBtn} onClick={(e) => handleBookFlight(flightLeg, e)}>
                      {isRoundTrip ? (isSelected ? "Selected ✓" : "Select") : "Book Now"}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className={styles.expandedRow} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.expandTabs}>
                      {["fares", "baggage", "itinerary"].map((t) => (
                        <button key={t} className={`${styles.expandTab} ${expandTab === t ? styles.expandTabActive : ""}`} onClick={() => setExpandTab(t)}>
                          {t === "fares" ? "Fare Options" : t === "baggage" ? "Baggage Info" : "Flight Details"}
                        </button>
                      ))}
                    </div>

                    {/* Fares */}
                    {expandTab === "fares" && priceDetails.length > 0 && (
                      <div className={styles.fareGrid}>
                        {priceDetails.map((fc, fi) => (
                          <div key={fi} className={`${styles.fareCard} ${selectedFareIdx === fi ? styles.fareCardSelected : ""}`} onClick={() => setSelectedFareIdxMap((p) => ({ ...p, [flightLeg.supplierLegId]: fi }))}>
                            <div className={styles.fareCardName}>{fc.fareIdentifier || "PUBLISHED"}</div>
                            <div className={styles.fareCardPrice}>{currencySymbol}{formatPrice(fc.totalPrice)}</div>
                            <div className={styles.fareCardFeatures}>
                              <div className={styles.fareFeature}><RiSuitcaseLine className={`${styles.fareFeatureIcon} ${styles.fareFeatureOk}`} />Check-in: {fc.checkinBag || "20 Kg"}</div>
                              <div className={styles.fareFeature}><RiSuitcaseLine className={`${styles.fareFeatureIcon} ${styles.fareFeatureOk}`} />Cabin: {fc.cabinBaggage || "7 Kg"}</div>
                              <div className={styles.fareFeature}>
                                {fc.dcAdults?.mealType === "Free Meal"
                                  ? <RiCheckLine className={`${styles.fareFeatureIcon} ${styles.fareFeatureOk}`} />
                                  : <RiCloseLine className={`${styles.fareFeatureIcon} ${styles.fareFeatureNo}`} />}
                                Meal {fc.dcAdults?.mealType === "Free Meal" ? "Included" : "Not included"}
                              </div>
                              <div className={styles.fareFeature}>
                                <RiRefreshLine className={`${styles.fareFeatureIcon} ${fc.dcAdults?.refundable === "Refundable" ? styles.fareFeatureOk : styles.fareFeatureNo}`} />
                                {fc.dcAdults?.refundable === "Refundable" ? "Refundable" : "Non-refundable"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Baggage */}
                    {expandTab === "baggage" && (
                      <div className={styles.baggageRow}>
                        {[
                          { icon: "🧳", label: "Check-in Baggage", value: fare?.checkinBag || "20 Kg", sub: "Per passenger" },
                          { icon: "🎒", label: "Cabin Baggage", value: fare?.cabinBaggage || "7 Kg", sub: "1 piece allowed" },
                          { icon: "🍽️", label: "Meal", value: fare?.dcAdults?.mealType === "Free Meal" ? "Included" : "Not Included", sub: fare?.dcAdults?.mealType === "Free Meal" ? "Complimentary" : "Purchase on board" },
                        ].map((b, bi) => (
                          <div key={bi} className={styles.baggageItem}>
                            <div className={styles.baggageIcon}>{b.icon}</div>
                            <div>
                              <div className={styles.baggageLabel}>{b.label}</div>
                              <div className={styles.baggageValue}>{b.value}</div>
                              <div className={styles.baggageSub}>{b.sub}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Itinerary */}
                    {expandTab === "itinerary" && (
                      <div className={styles.itineraryWrap}>
                        {flightLeg.dcSegments?.map((seg, sIdx) => (
                          <div key={sIdx} className={styles.itinerarySegment}>
                            <div className={styles.itineraryAirlineRow}>
                              <img src={`/assets/images/airlinelogos/${seg?.tFAirlineData?.airlineCode || "default"}.png`} alt={seg?.tFAirlineData?.airlineCode} className={styles.itineraryLogoImg} onError={(e) => { e.target.style.display = "none"; }} />
                              <div>
                                <div className={styles.itineraryAirlineName}>{seg?.tFAirlineData?.airline} <span className={styles.itineraryFlightNum}>| {seg?.tFAirlineData?.flightNumber}</span></div>
                                <div className={styles.itineraryEquip}>Aircraft: {seg?.tFAirlineData?.equipmentType || "N/A"}</div>
                              </div>
                            </div>

                            <div className={styles.itineraryRoute}>
                              <div className={styles.itineraryTimeBlock}>
                                <div className={styles.itineraryTime}>{seg?.tFDepartureData?.departureDateTime ? moment(seg.tFDepartureData.departureDateTime).format("HH:mm") : "--:--"}</div>
                                <div className={styles.itineraryCode}>{seg?.tFDepartureData?.airportCode}</div>
                                <div className={styles.itineraryTerminal}>Terminal {seg?.tFDepartureData?.terminal || "-"}</div>
                              </div>
                              <div className={styles.itineraryMid}>
                                <div className={styles.itineraryDur}>{formatDuration(seg?.tFAirlineData?.duration)}</div>
                                <div className={styles.itineraryLine}><RiPlaneLine className={styles.itineraryPlane} /></div>
                              </div>
                              <div className={styles.itineraryTimeBlock}>
                                <div className={styles.itineraryTime}>{seg?.tFArrivalData?.arrivalDateTime ? moment(seg.tFArrivalData.arrivalDateTime).format("HH:mm") : "--:--"}</div>
                                <div className={styles.itineraryCode}>{seg?.tFArrivalData?.airportCode}</div>
                                <div className={styles.itineraryTerminal}>Terminal {seg?.tFArrivalData?.terminal || "-"}</div>
                              </div>
                            </div>

                            {sIdx < flightLeg.dcSegments.length - 1 && (
                              <div className={styles.layoverBar}>
                                <RiTimeLine className={styles.layoverIcon} />
                                Layover in {flightLeg.dcSegments[sIdx]?.tFArrivalData?.city || "Unknown"}: {formatDuration(flightLeg.dcSegments[sIdx + 1]?.tFAirlineData?.connectingTime)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Toggle chevron */}
                <div className={styles.toggleRow}>
                  <button className={styles.toggleBtn} onClick={(e) => { e.stopPropagation(); toggleExpand(flightLeg.supplierLegId); }}>
                    {isExpanded ? "Hide Details" : "View Fare Options & Itinerary"}
                    <RiArrowDownSLine className={`${styles.toggleIcon} ${isExpanded ? styles.toggleIconOpen : ""}`} />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {!pageLoading && totalPages > 1 && (
            <div className={styles.pagination}>
              <button className={styles.pageBtn} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} className={`${styles.pageBtn} ${page === p ? styles.pageBtnActive : ""}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className={styles.pageBtn} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
            </div>
          )}

          {/* Round-Trip Summary Block */}
          {isRoundTrip && Object.keys(selectedFaresRoundTrip).length > 0 && (
            <div className={styles.roundTripSummary}>
              <div className={styles.roundTripTitle}>Selected Flights Summary</div>
              {selectedFaresRoundTrip.outbound && (
                <div className={styles.roundTripRow}>
                  <span>Outbound: {selectedFaresRoundTrip.outbound.locationFrom} → {selectedFaresRoundTrip.outbound.locationTo}</span>
                  <span className={styles.roundTripPrice}>{currencySymbol} {formatPrice(selectedFaresRoundTrip.outbound.fare?.totalPrice)}</span>
                </div>
              )}
              {selectedFaresRoundTrip.return && (
                <div className={`${styles.roundTripRow} ${styles.roundTripRowBorder}`}>
                  <span>Return: {selectedFaresRoundTrip.return.locationFrom} → {selectedFaresRoundTrip.return.locationTo}</span>
                  <span className={styles.roundTripPrice}>{currencySymbol} {formatPrice(selectedFaresRoundTrip.return.fare?.totalPrice)}</span>
                </div>
              )}
              {selectedFaresRoundTrip.outbound && selectedFaresRoundTrip.return && (
                <div className={styles.roundTripTotal}>
                  <span className={styles.roundTripTotalLabel}>Total Price</span>
                  <div className={styles.roundTripTotalRight}>
                    <span className={styles.roundTripTotalPrice}>
                      {currencySymbol} {formatPrice((selectedFaresRoundTrip.outbound.fare?.totalPrice || 0) + (selectedFaresRoundTrip.return.fare?.totalPrice || 0))}
                    </span>
                    <button className={styles.bookBtn} style={{ marginTop: 0, width: "auto", padding: "10px 24px" }} onClick={handleProceedToBooking}>
                      Continue to Book
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Flights5;